import { forwardRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import SuiBox from "components/SuiBox";
import styles from "components/SuiInput/styles";
import { TextField } from "@mui/material";

const SuiInput = forwardRef(
  (
    {
      size,
      withIcon,
      inputSettungs,
      error,
      success,
      customClass,
      disabled,
      inputProps,
      onChange,
      onBlur,
      onKeyPress,
      ...rest
    },
    ref
  ) => {
    let template;
    const { baseClass, color, svg, icon, onClickIcon } = withIcon;

    // handle special keywords in number input
    function handleKeyDown(e){
      const { type } = rest;

      if(typeof onKeyPress === 'function'){
        onKeyPress(e)
      }

      if (type !== 'number') return;

      if (e.key === "e" || e.key === "E") {
        e.preventDefault()
      }
    }

    const iconBaseClass = !baseClass ? "" : `-${baseClass}`;
    const iconColor = !color ? "dark" : color;
    const direction = "ltr";
    const classes = styles({
      size,
      error,
      success,
      withIcon,
      direction,
      disabled,
    });
    if (withIcon.direction === "left") {
      template = (
        <SuiBox className={clsx(classes.suiInputIcon, customClass)}>
          <IconButton type="button" onClick={onClickIcon} className={classes.suiInputIcon_right}>
            {icon ? (
              <Icon
                color={iconColor}
                className={classes.suiInputIcon_icon}
                baseClassName={`material-icons${iconBaseClass}`}
                fontSize="small"
              >
                {withIcon.icon}
              </Icon>
            ) : (
              svg
            )}
          </IconButton>
          <TextField
            {...rest}
            autoComplete={inputSettungs.autoComplete}
            required={inputSettungs.required}
            inputProps={{
              ref: ref,
              ...inputProps,
            }}
            className={clsx(classes.suiInput, classes.suiInputIcon_input, {
              [classes.suiInput_error]: error,
              [classes.suiInput_success]: success,
              [classes[`suiInput_${size}`]]: size,
            })}
            classes={{
              focused: classes.suiInput_focused,
              disabled: classes.suiInput_disabled,
              error: classes.suiInput_error,
              multiline: classes.suiInput_multiline,
            }}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            onKeyDown={handleKeyDown}
          />
        </SuiBox>
      );
    } else if (withIcon.direction === "right") {
      template = (
        <SuiBox className={clsx(classes.suiInputIcon, customClass)}>
          <TextField
            {...rest}
            autoComplete={inputSettungs.autoComplete}
            required={inputSettungs.required}
            inputProps={{
              ref: ref,
              ...inputProps,
            }}
            className={clsx(classes.suiInput, classes.suiInputIcon_input, {
              [classes.suiInput_error]: error,
              [classes.suiInput_success]: success,
              [classes[`suiInput_${size}`]]: size,
            })}
            classes={{
              focused: classes.suiInput_focused,
              disabled: classes.suiInput_disabled,
              error: classes.suiInput_error,
              multiline: classes.suiInput_multiline,
            }}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            onKeyDown={handleKeyDown}
          />
          <IconButton type="button" onClick={onClickIcon} className={classes.suiInputIcon_right}>
            {icon ? (
              <Icon
                color={iconColor}
                className={classes.suiInputIcon_icon}
                baseClassName={`material-icons${iconBaseClass}`}
                fontSize="small"
              >
                {withIcon.icon}
              </Icon>
            ) : (
              svg
            )}
          </IconButton>
        </SuiBox>
      );
    } else {
      template = (
        <TextField
          {...rest}
          autoComplete={inputSettungs.autoComplete}
          required={inputSettungs.required}
          inputProps={{
            ref: ref,
            // sx: { "&[type='text']": { textTransform: "capitalize" } },
            ...inputProps,
          }}
          className={clsx(classes.suiInput, customClass, {
            [classes.suiInput_error]: error,
            [classes.suiInput_success]: success,
            [classes[`suiInput_${size}`]]: size,
          })}
          classes={{
            focused: classes.suiInput_focused,
            disabled: classes.suiInput_disabled,
            error: classes.suiInput_error,
            multiline: classes.suiInput_multiline,
          }}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          onKeyDown={handleKeyDown}
        />
      );
    }

    return template;
  }
);

SuiInput.defaultProps = {
  size: "medium",
  withIcon: {
    icon: false,
    direction: "none",
    onClickIcon: (e) => e.preventDefault(),
  },
  inputSettungs: {
    autoComplete: "on",
    required: false,
  },
  error: false,
  success: false,
  customClass: "",
  disabled: false,
};

SuiInput.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  withIcon: PropTypes.shape({
    baseClass: PropTypes.oneOf(["outlined", "rounded", "two-tone", "sharp"]),
    color: PropTypes.oneOf([
      "white",
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
    direction: PropTypes.oneOf(["none", "left", "right"]),
    onClickIcon: PropTypes.func,
  }),
  inputSettungs: PropTypes.shape({
    autoComplete: PropTypes.string,
    required: PropTypes.bool,
  }),
  inputProps: PropTypes.object,
  error: PropTypes.bool,
  success: PropTypes.bool,
  customClass: PropTypes.string,
  disabled: PropTypes.bool,
};

export default SuiInput;
